import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRightIcon,
  ChartBarIcon,
  Squares2X2Icon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { useAuth } from '@/stores/auth';
import { useAccessibleModules } from '@/hooks/useAccessibleModules';
import { homeService } from '@/services/home.service';
import type { HomeModule } from '@/config/home-modules';

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Administrator',
  ADMIN: 'Administrator',
  SECURITY_MANAGER: 'Security Manager',
  ANALYST: 'Analyst',
  OPERATOR: 'Operator',
  VIEWER: 'Viewer',
  LEGAL_ADMIN: 'Legal & Compliance Administrator',
};

const ROLE_HIERARCHY = [
  'SUPER_ADMIN',
  'ADMIN',
  'SECURITY_MANAGER',
  'LEGAL_ADMIN',
  'ANALYST',
  'OPERATOR',
  'VIEWER',
];

function highestRoleLabel(roleNames: string[]): string | null {
  for (const role of ROLE_HIERARCHY) {
    if (roleNames.includes(role)) {
      return ROLE_LABELS[role] ?? role;
    }
  }
  return roleNames[0] ?? null;
}

function ModuleIndicators({ module }: { module: HomeModule }) {
  const { data: indicators, isLoading } = useQuery({
    queryKey: ['home-indicators', module.indicatorSource],
    queryFn: () => homeService.getIndicators(module.indicatorSource),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3" data-testid={`indicators-loading-${module.id}`}>
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-16 rounded-lg bg-secondary-100 dark:bg-secondary-700/40 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (!indicators || indicators.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      {indicators.map((indicator) => (
        <div
          key={indicator.label}
          className="rounded-lg border border-secondary-200 dark:border-secondary-700 px-3 py-2"
        >
          <div className="text-xl font-semibold text-secondary-900 dark:text-white">
            {indicator.value}
          </div>
          <div className="text-xs text-secondary-500 dark:text-secondary-400">
            {indicator.label}
          </div>
        </div>
      ))}
    </div>
  );
}

function ModuleCard({ module, single }: { module: HomeModule; single: boolean }) {
  const Icon = module.icon;
  return (
    <Card
      variant="default"
      hover="default"
      className={cn('flex flex-col', single && 'md:col-span-2')}
      data-testid="module-card"
      data-module-id={module.id}
    >
      <div className="flex items-start gap-4">
        <div className={cn('flex-shrink-0 rounded-xl p-3', module.accentClass)}>
          <Icon className="h-7 w-7" />
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
            {module.label}
          </h2>
          <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
            {module.description}
          </p>
        </div>
      </div>

      <div className="mt-5">
        <ModuleIndicators module={module} />
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <Link to={module.enterTo} className="sm:flex-1" aria-label={`Enter ${module.label}`}>
          <Button className="w-full" tabIndex={-1}>
            Enter module
            <ArrowRightIcon className="ml-2 h-4 w-4" />
          </Button>
        </Link>
        <div className="flex gap-3">
          <Link
            to={module.dashboardTo}
            className="flex-1 sm:flex-none"
            aria-label={`${module.label} dashboard`}
          >
            <Button variant="outline" className="w-full" tabIndex={-1}>
              <Squares2X2Icon className="mr-2 h-4 w-4" />
              Dashboard
            </Button>
          </Link>
          <Link
            to={module.analyticsTo}
            className="flex-1 sm:flex-none"
            aria-label={`${module.label} analytics`}
          >
            <Button variant="outline" className="w-full" tabIndex={-1}>
              <ChartBarIcon className="mr-2 h-4 w-4" />
              Analytics
            </Button>
          </Link>
        </div>
      </div>
    </Card>
  );
}

export default function Home() {
  const { user, isLoading } = useAuth();
  const modules = useAccessibleModules();

  const roleNames = user?.roles.map((r) => r.name) ?? [];
  const roleLabel = highestRoleLabel(roleNames);
  const firstName = user?.firstName?.trim();

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6" data-testid="home-loading">
        <div className="space-y-4">
          <div className="h-8 w-64 rounded bg-secondary-100 dark:bg-secondary-700/40 animate-pulse" />
          <div className="grid gap-6 md:grid-cols-2">
            {[0, 1].map((i) => (
              <div
                key={i}
                className="h-56 rounded-xl bg-secondary-100 dark:bg-secondary-700/40 animate-pulse"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="space-y-8"
      >
        {/* Intro band */}
        <header>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
            {firstName ? `Welcome back, ${firstName}` : 'Welcome'}
          </h1>
          <p className="mt-2 max-w-3xl text-secondary-600 dark:text-secondary-400">
            Your unified platform for corporate security and legal &amp; compliance operations. Pick
            a module below to jump into its dashboard, analytics, and day-to-day workflows.
          </p>
        </header>

        {modules.length === 0 ? (
          /* Empty state: authenticated but no module access */
          <Card className="text-center" data-testid="home-empty-state">
            <Squares2X2Icon className="mx-auto h-10 w-10 text-secondary-400" />
            <h2 className="mt-4 text-lg font-semibold text-secondary-900 dark:text-white">
              No modules available yet
            </h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-secondary-600 dark:text-secondary-400">
              Your account doesn&apos;t have access to any modules right now. Contact your
              administrator to have a module enabled for you.
            </p>
          </Card>
        ) : (
          <>
            {/* Your modules */}
            <section aria-labelledby="your-modules-heading">
              <h2
                id="your-modules-heading"
                className="mb-4 text-sm font-semibold uppercase tracking-wider text-secondary-500 dark:text-secondary-400"
              >
                Your modules
              </h2>
              <div className="grid gap-6 md:grid-cols-2">
                {modules.map((module) => (
                  <ModuleCard key={module.id} module={module} single={modules.length === 1} />
                ))}
              </div>
            </section>

            {/* What you can do here — role-aware capability summary */}
            <section aria-labelledby="capabilities-heading">
              <h2
                id="capabilities-heading"
                className="mb-4 text-sm font-semibold uppercase tracking-wider text-secondary-500 dark:text-secondary-400"
              >
                What you can do here
              </h2>
              <Card>
                <div className="flex items-center gap-2 text-secondary-700 dark:text-secondary-300">
                  <SparklesIcon className="h-5 w-5 text-primary-500" />
                  <p className="text-sm">
                    {roleLabel ? (
                      <>
                        You&apos;re signed in as a{' '}
                        <span className="font-semibold">{roleLabel}</span> with access to{' '}
                        {modules.length === 1
                          ? 'one module'
                          : `${modules.length} modules`}
                        .
                      </>
                    ) : (
                      <>You have access to the modules listed below.</>
                    )}
                  </p>
                </div>
                <ul className="mt-4 space-y-2">
                  {modules.map((module) => (
                    <li
                      key={module.id}
                      className="flex items-start gap-2 text-sm text-secondary-600 dark:text-secondary-400"
                    >
                      <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-500" />
                      <span>
                        <span className="font-medium text-secondary-800 dark:text-secondary-200">
                          {module.label}:
                        </span>{' '}
                        {module.capabilitySummary}
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>
            </section>
          </>
        )}
      </motion.div>
    </div>
  );
}
